// duration-input.tsx — duration field that accepts hour formats + quick presets,
// so users never have to hand-convert hours into minutes. Stores minutes.
import { useEffect, useState } from "react";
import { getDayCapacityMinutes } from "../lib/app-prefs";
import { formatDuration, parseDurationToMinutes } from "./duration-derive";

/** Preset minute values offered as quick chips (besides "Cả ngày"). */
const PRESETS = [15, 30, 60, 120, 240];
const presetLabel = (m: number) => (m < 60 ? `${m}p` : `${m / 60}h`);

const chipCls = (active: boolean) =>
  `shrink-0 rounded-lg px-2.5 py-1 text-[12px] font-bold transition ${
    active ? "bg-brand-500 text-white" : "bg-ink-200/50 text-ink-600 hover:bg-ink-200"
  }`;

export function DurationInput({
  value, onChange, max = 1440, className = "",
}: { value: number; onChange: (minutes: number) => void; max?: number; className?: string }) {
  // Local text buffer so the user can type "1h30" freely; reflects the stored
  // value (formatted) whenever the field is not being edited.
  const [text, setText] = useState(() => formatDuration(value));
  const [focused, setFocused] = useState(false);
  useEffect(() => { if (!focused) setText(formatDuration(value)); }, [value, focused]);

  const dayCap = getDayCapacityMinutes();
  const commit = (min: number) => onChange(Math.min(max, Math.max(0, Math.round(min))));

  return (
    <div>
      <input
        className={className}
        value={text}
        inputMode="text"
        placeholder="vd: 30p, 1h30, 2 giờ"
        onFocus={() => setFocused(true)}
        onChange={(e) => {
          setText(e.target.value);
          const n = parseDurationToMinutes(e.target.value);
          if (n !== null) commit(n);
        }}
        onBlur={() => {
          setFocused(false);
          const n = parseDurationToMinutes(text);
          if (n === null) {
            setText(formatDuration(value)); // unparseable → revert to stored
          } else {
            const clamped = Math.min(max, Math.max(0, n));
            commit(clamped);
            setText(formatDuration(clamped));
          }
        }}
      />
      <div className="mt-1.5 flex flex-wrap gap-1.5">
        {PRESETS.map((m) => (
          <button key={m} type="button" onClick={() => commit(m)} className={chipCls(value === m)}>
            {presetLabel(m)}
          </button>
        ))}
        <button type="button" onClick={() => commit(dayCap)} className={chipCls(value === dayCap)}>
          Cả ngày
        </button>
      </div>
    </div>
  );
}
